import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CategoryGroup } from "@/types/category";
import { toast } from "@/hooks/use-toast";

export const useCategoryGroups = () => {
  const queryClient = useQueryClient();

  const { data: categoryGroups, isLoading } = useQuery({
    queryKey: ["categoryGroups"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("category_groups")
        .select(`
          *,
          categories!inner (
            id,
            quote_categories!inner (
              quote_id,
              quotes (
                id
              )
            )
          )
        `)
        .order("name");
        
      if (error) throw error;
      
      return data.map(group => ({
        ...group,
        quoteCount: group.categories?.reduce((total, cat) => 
          total + (cat.quote_categories?.filter(qc => qc.quotes).length || 0), 0) || 0
      }));
    },
    staleTime: 0, // Remove stale time to ensure fresh data on first load
    gcTime: 30 * 60 * 1000,
  });

  const mutation = useMutation({
    mutationFn: async (group: { id?: string; name: string; description: string | null }) => {
      if (group.id) {
        const { error } = await supabase
          .from("category_groups")
          .update({ 
            name: group.name,
            description: group.description
          })
          .eq("id", group.id);
          
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("category_groups")
          .insert([{ 
            name: group.name,
            description: group.description
          }]);
          
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categoryGroups"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("category_groups")
        .delete()
        .eq("id", id);
        
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categoryGroups"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast({
        title: "Category group deleted",
        description: "The category group has been deleted successfully."
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message
      });
    }
  });

  return {
    categoryGroups,
    isLoading,
    mutation,
    deleteMutation
  };
};
